import * as THREE from 'three';
import { Track, makeFrame, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';
import { facadeTexture, streakSprite } from '../proctex.js';

/**
 * NEON HAVEN — rain-soaked megacity at night. Instanced towers with lit windows,
 * holo-arches over the road, falling rain around the camera, wet-look deck lighting.
 */
export function buildNeonHaven(scene: THREE.Scene, track: Track, quality: GraphicsPreset): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng('neonhaven');

  // ── sky: deep blue-purple gradient with city glow at horizon ──
  {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {},
      vertexShader: `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec3 vDir;
        void main(){
          float h = vDir.y * 0.5 + 0.5;
          vec3 zenith = vec3(0.012, 0.014, 0.045);
          vec3 horizonGlow = vec3(0.10, 0.05, 0.16);
          vec3 cityGlow = vec3(0.16, 0.10, 0.20);
          vec3 col = mix(horizonGlow, zenith, pow(h, 0.9));
          col += cityGlow * pow(max(0.0, 1.0 - h*2.2), 2.0);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
  }

  // ── lights: cool ambient + neon-tinted key ──
  {
    const hemi = new THREE.HemisphereLight(0x2a3d66, 0x0c0a14, 0.85);
    const key = new THREE.DirectionalLight(0x8ab8ff, 0.75);
    key.position.set(300, 800, -200);
    const fill = new THREE.DirectionalLight(0xff2d6f, 0.18);
    fill.position.set(-500, 200, 400);
    scene.add(hemi, key, fill);
    objects.push(hemi, key, fill);
  }

  // ── track bounds ──
  const frame = makeFrame();
  let cx = 0, cz = 0, minY = Infinity;
  const SAMPLES = 80;
  const ring: { x: number; y: number; z: number }[] = [];
  for (let i = 0; i < SAMPLES; i++) {
    track.main.frameAt((i / SAMPLES) * track.length, frame);
    ring.push({ x: frame.pos.x, y: frame.pos.y, z: frame.pos.z });
    cx += frame.pos.x;
    cz += frame.pos.z;
    minY = Math.min(minY, frame.pos.y);
  }
  cx /= SAMPLES;
  cz /= SAMPLES;
  const groundY = minY - 55;

  // ── city towers (instanced, window facades) ──
  {
    const count = quality === 'performance' ? 120 : quality === 'balanced' ? 260 : 380;
    const facTex = facadeTexture(11);
    disposables.push(facTex);
    const geo = new THREE.BoxGeometry(1, 1, 1);
    const mat = new THREE.MeshStandardMaterial({
      map: facTex,
      emissive: 0xffffff,
      emissiveMap: facTex,
      emissiveIntensity: 0.85,
      color: 0x14161c,
      roughness: 0.8,
      metalness: 0.2,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const dummy = new THREE.Object3D();
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const x = cx + rng.range(-1400, 1400);
      const z = cz + rng.range(-1400, 1400);
      // keep towers clear of the ribbon
      let tooClose = false;
      for (const p of ring) {
        const d = Math.hypot(p.x - x, p.z - z);
        if (d < 90) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) continue;
      const h = rng.range(60, 420);
      const w = rng.range(22, 60);
      dummy.position.set(x, groundY + h / 2, z);
      dummy.scale.set(w, h, rng.range(22, 60));
      dummy.rotation.y = rng.chance(0.5) ? 0 : Math.PI / 4;
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo, mat);
  }

  // ── ground: dark wet city floor ──
  {
    const geo = new THREE.PlaneGeometry(6000, 6000);
    const mat = new THREE.MeshStandardMaterial({ color: 0x05070c, roughness: 0.35, metalness: 0.6 });
    const floor = new THREE.Mesh(geo, mat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(cx, groundY, cz);
    scene.add(floor);
    objects.push(floor);
    disposables.push(geo, mat);
  }

  // ── holo arches over the track ──
  const archMats: THREE.MeshBasicMaterial[] = [];
  {
    const archCount = Math.floor(track.length / 210);
    const geo = new THREE.TorusGeometry(1, 0.055, 6, 28, Math.PI);
    disposables.push(geo);
    const palette = [0x31c8ff, 0xff2d6f, 0xffd25e, 0x52e89a];
    for (let i = 0; i < archCount; i++) {
      const s = (i / archCount) * track.length;
      track.main.frameAt(s, frame);
      const mat = new THREE.MeshBasicMaterial({
        color: palette[i % palette.length],
        transparent: true,
        opacity: 0.75,
        toneMapped: false,
      });
      archMats.push(mat);
      disposables.push(mat);
      const arch = new THREE.Mesh(geo, mat);
      const r = frame.width * 0.62;
      arch.scale.set(r, r, r);
      arch.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
      const m = new THREE.Matrix4().makeBasis(
        new THREE.Vector3(frame.right.x, frame.right.y, frame.right.z),
        new THREE.Vector3(frame.up.x, frame.up.y, frame.up.z),
        new THREE.Vector3(frame.tangent.x, frame.tangent.y, frame.tangent.z)
      );
      arch.quaternion.setFromRotationMatrix(m);
      scene.add(arch);
      objects.push(arch);
    }
  }

  // ── rain (instanced streaks around the camera) ──
  const rainCount = quality === 'performance' ? 260 : 700;
  const rain = (() => {
    const geo = new THREE.PlaneGeometry(0.03, 1.6);
    const mat = new THREE.MeshBasicMaterial({
      map: streakSprite(),
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0x9ab8d8,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, rainCount);
    mesh.frustumCulled = false;
    disposables.push(geo, mat);
    const offsets: THREE.Vector3[] = [];
    for (let i = 0; i < rainCount; i++) {
      offsets.push(new THREE.Vector3(rng.range(-40, 40), rng.range(-25, 25), rng.range(-40, 40)));
    }
    scene.add(mesh);
    objects.push(mesh);
    return { mesh, offsets };
  })();
  const rainDummy = new THREE.Object3D();

  let t = 0;
  return {
    update(dt, camPos, _time) {
      t += dt;
      // arch pulse
      for (let i = 0; i < archMats.length; i++) {
        archMats[i].opacity = 0.55 + Math.sin(t * 2 + i * 1.7) * 0.25;
      }
      // rain falls in camera-local box, wraps vertically
      for (let i = 0; i < rain.offsets.length; i++) {
        const o = rain.offsets[i];
        o.y -= dt * 38;
        if (o.y < -25) o.y += 50;
        rainDummy.position.set(camPos.x + o.x, camPos.y + o.y, camPos.z + o.z);
        rainDummy.rotation.set(0, ((i * 37) % 100) * 0.0628, 0.12);
        rainDummy.updateMatrix();
        rain.mesh.setMatrixAt(i, rainDummy.matrix);
      }
      rain.mesh.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
